import {
  type ChatBatteryState,
  type ChatCitation,
  type ChatClarificationRequest,
  type ChatQuota,
  ChatStreamParser,
  type PendingAction,
} from '@nutians/shared/schemas';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api.js';

export interface ChatMessageState {
  localId: string;
  role: 'user' | 'assistant';
  content: string;
  citations: ChatCitation[];
  pendingActions: PendingAction[];
  clarifications: ChatClarificationRequest[];
  actionsAreLive: boolean;
  isStreaming: boolean;
  error?: string;
}

const env = import.meta.env as unknown as Record<string, string | undefined>;
const API_BASE = env.VITE_API_BASE_URL ?? '/api';

export function useChatStream() {
  const [messages, setMessages] = useState<ChatMessageState[]>([]);
  const [quota, setQuota] = useState<ChatQuota | null>(null);
  const [battery, setBattery] = useState<ChatBatteryState | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const isStreamingRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const refreshBattery = useCallback(async () => {
    const { data, response } = await api.GET('/me/chat-battery', {});
    if (response.ok) setBattery(data ?? null);
  }, []);

  useEffect(() => {
    void refreshBattery();
  }, [refreshBattery]);

  const checkIn = useCallback(async () => {
    const { data, response } = await api.POST('/me/check-in', {});
    if (!response.ok || !data) throw new Error(`HTTP ${response.status}`);
    setBattery(data.battery);
    return data;
  }, []);

  const loadThread = useCallback(async (threadId: string) => {
    const { data, response } = await api.GET('/me/chat/{threadId}/messages', {
      params: { path: { threadId } },
    });
    if (!response.ok || !data) throw new Error(`HTTP ${response.status}`);
    setMessages(
      data.messages.map((message) => ({
        localId: message.id,
        role: message.role,
        content: message.content,
        citations: message.citations,
        pendingActions: message.pendingActions,
        clarifications: message.clarifications as ChatClarificationRequest[],
        actionsAreLive: message.actionsAreLive,
        isStreaming: false,
      })),
    );
  }, []);

  const clearMessages = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
  }, []);

  const send = useCallback(
    async (text: string, threadId?: string) => {
      if (isStreamingRef.current) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      isStreamingRef.current = true;
      setIsStreaming(true);

      const assistantLocalId = crypto.randomUUID();

      setMessages((prev) => [
        ...prev,
        {
          localId: crypto.randomUUID(),
          role: 'user',
          content: text,
          citations: [],
          pendingActions: [],
          clarifications: [],
          actionsAreLive: true,
          isStreaming: false,
        },
        {
          localId: assistantLocalId,
          role: 'assistant',
          content: '',
          citations: [],
          pendingActions: [],
          clarifications: [],
          actionsAreLive: true,
          isStreaming: true,
        },
      ]);

      try {
        const response = await fetch(`${API_BASE}/me/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: text,
            ...(threadId ? { threadId } : {}),
          }),
          credentials: 'include',
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`HTTP ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        const parser = new ChatStreamParser();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const events = parser.push(decoder.decode(value, { stream: true }));

          for (const event of events) {
            if (event.type === 'token') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.localId === assistantLocalId ? { ...m, content: m.content + event.text } : m,
                ),
              );
            } else if (event.type === 'citation') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.localId === assistantLocalId
                    ? { ...m, citations: [...m.citations, event.citation] }
                    : m,
                ),
              );
            } else if (event.type === 'pending_action') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.localId === assistantLocalId
                    ? { ...m, pendingActions: [...m.pendingActions, event.action] }
                    : m,
                ),
              );
            } else if (event.type === 'clarification_request') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.localId === assistantLocalId
                    ? { ...m, clarifications: [...m.clarifications, event.clarification] }
                    : m,
                ),
              );
            } else if (event.type === 'done') {
              if (event.quota) setQuota(event.quota);
              await refreshBattery();
              setMessages((prev) =>
                prev.map((m) =>
                  m.localId === assistantLocalId ? { ...m, isStreaming: false } : m,
                ),
              );
            } else if (event.type === 'error') {
              if (event.battery) setBattery(event.battery);
              setMessages((prev) =>
                prev.map((m) =>
                  m.localId === assistantLocalId
                    ? { ...m, isStreaming: false, error: event.message }
                    : m,
                ),
              );
            }
          }
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setMessages((prev) =>
          prev.map((m) =>
            m.localId === assistantLocalId
              ? { ...m, isStreaming: false, error: 'Connection failed. Please try again.' }
              : m,
          ),
        );
      } finally {
        isStreamingRef.current = false;
        setIsStreaming(false);
      }
    },
    [refreshBattery],
  );

  const confirmAction = useCallback(
    async (
      actionId: string,
      decision: 'accept' | 'reject' | 'select',
      selected?: unknown[],
    ): Promise<void> => {
      const response = await fetch(`${API_BASE}/me/chat/actions/${actionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          decision === 'select' ? { decision, selected: selected ?? [] } : { decision },
        ),
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
    },
    [],
  );

  return {
    messages,
    quota,
    battery,
    isStreaming,
    send,
    confirmAction,
    checkIn,
    refreshBattery,
    loadThread,
    clearMessages,
  };
}
